import React from "react";
import { connect } from "react-redux";
import { I18n } from "@aws-amplify/core";
import { withStyles } from "@material-ui/styles";
import { get, size, debounce, filter, map, includes } from "lodash";
import clsx from "clsx";

import { fetchGet, fetchPost, fetchPut } from "../../../api/fetch";

import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";

import PictureROI from "../../Common/PictureROI";
import CustomButton from "../../Common/CustomButton";

import {
  getMeasureType,
  displayedTemperature,
  getMeasureUnit,
  //isOverThreshold,
  getDisplayedTemperatureAttr,
  //getThresholdEvent,
  getMaxThermalPicture,
} from "../../../utils/temperature";
import { getAttributeValue } from "../../../utils/profile";

import { ISO_TYPE, MANUAL_TYPE } from "../../../constants/common";

import colors from "../../../assets/style/colors";
import { dbFormat } from "../../../utils/date";
import Loader from "../../Common/Loader";
import InfoSection from "../../Common/InfoSection";

const styles = {
  colorCircle: {
    width: "14px",
    height: "14px",
    borderRadius: "50%",
  },
  cursorTooltip: {
    position: "absolute",
    width: "90px",
    height: "80px",
    padding: "8px",
    borderRadius: "3px",
    backgroundColor: colors.white,
    border: `1px solid ${colors.veryLighPink}`,
    zIndex: 200,
  },
  cursorTooltipAdd: {
    backgroundColor: colors.manualTempBlue,
    border: "none",
    color: colors.white,
  },
  sidebarItem: {
    height: "30px",
    display: "flex",
    alignItems: "center",
  },
  sidebarItemTitle: {
    color: colors.black87,
    fontWeight: 500,
    display: "inline-block",
    width: "75px",
  },
  pictureContainer: {
    position: "relative",
    height: "calc(100vh - 150px)",
    width: "calc(100vw - 400px)",
  },
  thermalROI: {
    width: "20px",
    height: "20px",
    border: `3px solid ${colors.blueROI}`,
    marginRight: "10px",
  },
  link: {
    color: colors.primary,
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  AddButton: {
    margin: "10px 0px",
    width: "100%",
  },
};

const ThermalPixel = (props) => {
  const { classes, powerMode, event, eventGroupId, lov } = props;
  const [cursorTemperature, setCursorTemperature] = React.useState();
  const [imgSize, setImgSize] = React.useState({ width: null, height: null });
  const [scale, setScale] = React.useState();
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState(1);
  const [showRoi, setShowRoi] = React.useState();
  const [cursorCoordinates, setCursorCoordinates] = React.useState({
    x: null,
    y: null,
  });
  const [addTemperature, setAddTemperature] = React.useState(false);
  const [newTemperature, setNewTemperature] = React.useState();
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    setShowRoi(true);
  }, []);

  const squareSize = {
    width: get(document.getElementById("pictureContainer"), ["offsetWidth"]),
    height: get(document.getElementById("pictureContainer"), ["offsetHeight"]),
  };
  const type = getMeasureType(event);
  const unit = getMeasureUnit(event, lov);
  const img = getMaxThermalPicture(event);

  const maxTemp = get(img, ["thermal_pixel_coordinate", "value"]);
  const jsonAttr = JSON.parse(getAttributeValue(event, 169));
  const jsonSample = get(
    filter(get(jsonAttr, ["samples"]), (s) => get(s, "value") === maxTemp),
    [0]
  );
  const blackbody = get(jsonSample, ["blackbody", "bb"]);

  const landmarks = get(jsonSample, ["landmarks"]);

  const debouncedMouseMove = (e, imageSize, newScale, offset, zoom) => {
    e.persist();
    debounce((e, imageSize, newScale, offset, zoom) => {
      let event = e;
      onMouseMove(event, imageSize, newScale, offset, zoom);
    }, 50)(e, imageSize, newScale, offset, zoom);
  };

  const onMouseMove = (e, imageSize, s, offset, z) => {
    if (!imgSize.width || !imgSize.height) {
      setImgSize({
        width: imageSize.width,
        height: imageSize.height,
      });
    }

    if (s !== scale) {
      setScale(s);
      setOffset(offset);
      setZoom(z);
    }

    const x1 = Math.round(scale * get(img, ["roi_overlay", "x1"]));
    const x2 = Math.round(scale * get(img, ["roi_overlay", "x2"]));
    const y1 = Math.round(scale * get(img, ["roi_overlay", "y1"]));
    const y2 = Math.round(scale * get(img, ["roi_overlay", "y2"]));

    const x = e.nativeEvent.offsetX - x1;
    const y = e.nativeEvent.offsetY - y1;

    if (
      x >= offset.x * scale &&
      x < x2 - x1 + offset.x * scale &&
      y >= offset.y * scale &&
      y < y2 - y1 + offset.y * scale
    ) {
      setCursorTemperature(
        get(img, [
          "roi_overlay",
          "temp_map",
          Math.round(
            x / scale - offset.x + ((x2 - x1) / scale) * (y / scale - offset.y)
          ),
        ])
      );
      setCursorCoordinates({
        x: e.nativeEvent.offsetX,
        y: e.nativeEvent.offsetY,
      });
    } else if (cursorCoordinates.x || cursorCoordinates.y) {
      setCursorTemperature(null);
      setCursorCoordinates({
        x: null,
        y: null,
      });
    }
  };

  const selectTemperature = (e) => {
    if (addTemperature) {
      const x1 = Math.round(scale * get(img, ["roi_overlay", "x1"]));
      const x2 = Math.round(scale * get(img, ["roi_overlay", "x2"]));
      const y1 = Math.round(scale * get(img, ["roi_overlay", "y1"]));
      const y2 = Math.round(scale * get(img, ["roi_overlay", "y2"]));

      const x = e.nativeEvent.offsetX - x1;
      const y = e.nativeEvent.offsetY - y1;

      if (
        x >= offset.x * scale &&
        x < x2 - x1 + offset.x * scale &&
        y >= offset.y * scale &&
        y < y2 - y1 + offset.y * scale
      ) {
        const selectedTemp = get(img, [
          "roi_overlay",
          "temp_map",
          Math.round(
            x / scale - offset.x + ((x2 - x1) / scale) * (y / scale - offset.y)
          ),
        ]);
        setNewTemperature({
          value: selectedTemp,
          x: e.nativeEvent.offsetX / scale - offset.x,
          y: e.nativeEvent.offsetY / scale - offset.y,
        });
      }
    }
  };

  const submitTemperature = async (replace = false) => {
    setIsSaving(true);
    try {
      // Duplicate event with new value
      let newEvent = {
        event_group_id: eventGroupId,
        type_id: get(event, ["type", "id"]),
        profile_id: get(event, ["profile", "id"]),
        attributes: map(
          filter(
            get(event, ["attributes"]),
            (attr) =>
              attr.value &&
              !includes([153, 164, 165, 166, 167, 168, 187], attr.id)
          ),
          (attr) => ({
            id: attr.id,
            value:
              attr.data_type === "datetime"
                ? dbFormat(new Date(attr.value), "YYYY-MM-DD[T]HH:mm:ss.S[Z]")
                : attr.value,
          })
        ),
      };
      newEvent.attributes.push({
        id: 187,
        value: get(newTemperature, ["value"]),
      });
      newEvent = await fetchPost("events", newEvent);

      // Copy photos
      for (let i = 0; i < size(get(event, ["images"])); i++) {
        let imgName = get(event, ["images", i, "url"]);
        if (imgName && imgName.includes("/api/images/")) {
          let index = imgName.indexOf("/api/images/");
          imgName = imgName.substring(index + 12);
        }
        let img = await fetchGet(`images/${imgName}`);
        let body = {
          name: imgName,
          enrollment_status: false,
          enrollment_error: false,
          main_display: false,
          "image-base64": img,
          roi_overlay: JSON.stringify(get(event, ["images", i, "roi_overlay"])),
          thermal_pixel_coordinate: JSON.stringify(newTemperature),
        };
        await fetchPost(`events/${get(newEvent, ["id"])}/images`, body);
        console.log("imageAdded");
      }

      if (replace) {
        console.log("replace");
        // Set current event to inactive
        await fetchPut(
          `event_groups/${eventGroupId}/events/${get(event, ["id"])}`,
          {
            active: false,
          }
        );
      }
    } catch (e) {
      console.log(e);
    }
    props.refresh();
    props.closeFunction();
  };

  const measureType = getMeasureType(event);
  const attr = getDisplayedTemperatureAttr(measureType);

  return (
    <Grid item style={{ margin: "0px 24px 24px" }}>
      <Grid container wrap="nowrap">
        <Grid item className={classes.pictureContainer} id="pictureContainer">
          {squareSize.width && squareSize.height && (
            <PictureROI
              url={get(img, ["url"])}
              roiOverlay={get(img, ["roi_overlay"])}
              thermalPoint={get(img, ["thermal_pixel_coordinate"])}
              initialSquareSize={squareSize}
              id={get(img, ["id"])}
              onMouseMove={debouncedMouseMove}
              showRoi={showRoi}
              onZoom={() => {}}
              blackbody={blackbody}
              landmarks={landmarks}
              onClick={selectTemperature}
              powerMode={powerMode}
              jsonSample={jsonSample}
            />
          )}
          {cursorCoordinates.x && cursorCoordinates.y && (
            <Box
              className={clsx(classes.cursorTooltip, {
                [classes.cursorTooltipAdd]: addTemperature,
              })}
              style={{
                top:
                  zoom > 1
                    ? 20 + cursorCoordinates.y - offset.y / (scale * zoom)
                    : 20 + cursorCoordinates.y,
                left:
                  zoom > 1
                    ? 20 + cursorCoordinates.x - offset.x / (scale * zoom)
                    : 20 + cursorCoordinates.x,
              }}
            >
              <Box
                className="bold-500 text-center"
                style={{ fontSize: "1.2em" }}
              >{`${cursorTemperature || "-"} ${unit}`}</Box>
              <Box>x: {cursorCoordinates.x}</Box>
              <Box>y: {cursorCoordinates.y}</Box>
            </Box>
          )}
        </Grid>
        <Grid item style={{ width: "250px", paddingLeft: "24px" }}>
          <Grid container direction="column">
            <Grid
              item
              className={classes.sidebarItem}
              style={{ marginBottom: "5px" }}
            >
              <Grid container alignItems="center">
                <Grid
                  item
                  className={classes.colorCircle}
                  style={{
                    backgroundColor:
                      type === ISO_TYPE
                        ? colors.isoYellow
                        : type === MANUAL_TYPE
                        ? colors.manualTempBlue
                        : colors.bodyPink,
                  }}
                />
                <Grid item className="text-54" style={{ marginLeft: "10px" }}>
                  {I18n.get(
                    type === ISO_TYPE
                      ? "site_isoTemp"
                      : type === MANUAL_TYPE
                      ? "site_manualTemperature"
                      : "site_bodyTemp"
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid item className={classes.sidebarItem}>
              <span
                style={{
                  display: "inline-block",
                  width: "20px",
                  height: "20px",
                  marginRight: "10px",
                  backgroundColor: colors.threatRed,
                }}
              />
              <span className={classes.sidebarItemTitle}>
                {I18n.get("site_temperatures")}
              </span>
            </Grid>
            <Grid
              item
              className={classes.sidebarItem}
              style={{
                fontSize: "1.857em",
                paddingLeft: "30px",
                marginBottom: "20px",
              }}
            >
              {displayedTemperature(event, attr, lov)}
            </Grid>
            {get(event, ["active"]) &&
              (isSaving ? (
                <Grid item style={{ marginTop: "40px" }}>
                  <Loader noDecoration message={I18n.get("profiles_saving")} />
                </Grid>
              ) : !addTemperature ? (
                <Grid
                  item
                  className={classes.sidebarItem}
                  style={{
                    paddingLeft: "22px",
                  }}
                >
                  <CustomButton
                    color="primary"
                    onClick={() => setAddTemperature(true)}
                  >
                    {I18n.get("site_newTemperature")}
                  </CustomButton>
                </Grid>
              ) : (
                <Grid item>
                  <Grid container direction="column">
                    <Grid item container alignItems="center">
                      <Grid
                        item
                        className={classes.colorCircle}
                        style={{
                          backgroundColor: colors.manualTempBlue,
                          width: "20px",
                          height: "20px",
                        }}
                      />
                      <Grid
                        item
                        className="bold-500"
                        style={{ marginLeft: "10px" }}
                      >
                        {I18n.get("site_newTemperature")}
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      style={{
                        fontSize: "1.857em",
                        paddingLeft: "30px",
                        height: "40px",
                      }}
                    >
                      {get(newTemperature, ["value"])
                        ? `${get(newTemperature, ["value"])}°C`
                        : ""}
                    </Grid>
                    <Grid
                      item
                      className="text-italic text-54"
                      style={{ margin: "10px 0px 40px" }}
                    >
                      {I18n.get("site_setTemperatureText")}
                    </Grid>
                    <Grid item>
                      <CustomButton
                        color="primary"
                        variant="contained"
                        className={classes.AddButton}
                        disabled={!get(newTemperature, ["value"])}
                        onClick={() => submitTemperature(false)}
                      >
                        {I18n.get("common_add")}
                      </CustomButton>
                    </Grid>
                    <Grid item>
                      <CustomButton
                        color="primary"
                        variant="contained"
                        className={classes.AddButton}
                        disabled={!get(newTemperature, ["value"])}
                        onClick={() => submitTemperature(true)}
                      >
                        {I18n.get("common_addAndReplace")}
                      </CustomButton>
                    </Grid>
                    <Grid item>
                      <CustomButton
                        color="primary"
                        onClick={() => setAddTemperature(false)}
                        className={classes.AddButton}
                      >
                        {I18n.get("site_cancel")}
                      </CustomButton>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            {powerMode && (
              <Grid
                item
                style={{
                  marginTop: "40px",
                  padding: "12px",
                  backgroundColor: colors.lightGrey,
                }}
              >
                <InfoSection
                  title2={I18n.get("site_temperatures")}
                  values={[`${get(jsonSample, ["value"])}°C`]}
                />
                <InfoSection
                  title2={I18n.get("site_eyesVisible")}
                  values={[get(jsonSample, ["eyes_visible"])]}
                />
                <InfoSection
                  title2={I18n.get("site_blackbodyState")}
                  values={[get(jsonSample, ["blackbody", "valid"])]}
                />
                <InfoSection
                  title2={I18n.get("site_blackbodyOffset")}
                  values={[get(jsonSample, ["blackbody", "offset"])]}
                />
                <InfoSection
                  title2={I18n.get("site_blackbodyRawValue")}
                  values={[get(jsonSample, ["blackbody", "value"])]}
                />
              </Grid>
            )}
            {/* <Grid item className={classes.sidebarItem}>
              <span className={classes.sidebarItemTitle}>
                {I18n.get("site_minimum")}:
              </span>
              <span className="text-54">
                {displayedTemperature(
                  event,
                  type === ISO_TYPE ? 167 : 164,
                  lov
                )}
              </span>
            </Grid>
            <Grid item className={classes.sidebarItem}>
              <span className={classes.sidebarItemTitle}>
                {I18n.get("site_maximum")}:
              </span>
              <span className="text-54">
                {displayedTemperature(
                  event,
                  type === ISO_TYPE ? 166 : 153,
                  lov
                )}
              </span>
              <span
                style={{
                  display: "inline-block",
                  width: "10px",
                  height: "10px",
                  marginLeft: "10px",
                  backgroundColor: colors.threatRed,
                }}
              />
            </Grid>
            <Grid item className={classes.sidebarItem}>
              <span className={classes.sidebarItemTitle}>
                {I18n.get("site_average")}:
              </span>
              <span
                className="flexCenter"
                style={{
                  justifyContent: "center",
                  width: "55px",
                  height: "24px",
                  borderRadius: "2px",
                  color: isOverThreshold(
                    getAttributeValue(event, getDisplayedTemperatureAttr(type)),
                    getThresholdEvent(event)
                  )
                    ? colors.threatRed
                    : colors.cardGreen,
                  backgroundColor: isOverThreshold(
                    getAttributeValue(event, getDisplayedTemperatureAttr(type)),
                    getThresholdEvent(event)
                  )
                    ? colors.threatRed10
                    : colors.cardGreen10,
                }}
              >
                {displayedTemperature(
                  event,
                  type === ISO_TYPE ? 168 : 165,
                  lov
                )}
              </span>
            </Grid> */}
            {/* <Grid item style={{ marginTop: "40px" }}>
              <Grid container alignItems="center">
                <Grid item className={classes.thermalROI} />
                <Grid
                  item
                  className={classes.link}
                  onClick={() => setShowRoi(!showRoi)}
                >
                  {`${I18n.get(showRoi ? "site_hide" : "site_show")} ${I18n.get(
                    "site_thermalROI"
                  )}`}
                </Grid>
              </Grid>
            </Grid> */}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state) => {
  return {
    powerMode: get(state, ["common", "powerUserModeEnabled"]),
  };
};

export default connect(mapStateToProps)(withStyles(styles)(ThermalPixel));
